import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ADFResponse } from '../../ai-session-context/types';

type RawADFNode = {
	attrs?: Record<string, unknown>;
	content?: RawADFNode[];
	type: string;
};

function findMatchingOrOnlyCell<T extends { attrs?: Record<string, unknown> }>(
	matches: T[],
	originalLocalId: unknown,
): T | undefined {
	const matchingId = matches.filter((node) => node.attrs?.localId === originalLocalId);
	if (matchingId.length === 1) {
		return matchingId[0];
	}
	return matchingId.length === 0 && matches.length === 1 ? matches[0] : undefined;
}

function findReplacementCell(content: Fragment, originalCell: PMNode): PMNode | undefined {
	const firstChild = content.firstChild;
	if (content.childCount === 1 && firstChild?.type === originalCell.type) {
		return firstChild;
	}

	const matches: PMNode[] = [];
	content.forEach((node) => {
		node.descendants((child) => {
			if (child.type === originalCell.type) {
				matches.push(child);
			}
		});
	});
	return findMatchingOrOnlyCell(matches, originalCell.attrs.localId);
}

function findRawCell(
	response: RawADFNode | undefined,
	originalCell: PMNode,
): RawADFNode | undefined {
	if (!response) {
		return undefined;
	}
	const directNode = response.type === 'doc' ? response.content?.[0] : response;
	if (directNode?.type === originalCell.type.name) {
		return directNode;
	}

	const matches: RawADFNode[] = [];
	const visit = (node: RawADFNode): void => {
		if (node.type === originalCell.type.name) {
			matches.push(node);
		}
		node.content?.forEach(visit);
	};
	visit(response);
	return findMatchingOrOnlyCell(matches, originalCell.attrs.localId);
}

/**
 * The replacement cell for a whole-cell replacement, or undefined when the value is
 * not a cell payload at all.
 *
 * Deliberately stricter than `findReplacementCell`: it peels only *synthetic*
 * single-child `table` / `tableRow` wrappers (which `ADFStreamer` adds because a bare
 * cell is not valid under `doc`) and requires a cell at the bottom. It does not fall
 * back to "the only cell anywhere in the value".
 *
 * That fallback is wrong here. A `tableRow` replacement selects the row, but a
 * `CellSelection` row selection reports a range identical to the first cell's inner
 * range, so positions alone cannot tell the two apart — see the `row replaceNode stays
 * on the generic path` case in `cellKindConversions.test.ts`. The value's shape can:
 * a row payload has `tableRow` on top with its cells as siblings, and must not have one
 * of those cells' ids copied onto the existing cell.
 */
function findReplacementCellForLocalId(
	content: Fragment,
	originalCell: PMNode,
): PMNode | undefined {
	const { table, tableRow } = originalCell.type.schema.nodes;
	let candidate = content.childCount === 1 ? content.firstChild : null;
	for (const wrapper of [table, tableRow]) {
		if (candidate && candidate.type === wrapper && candidate.childCount === 1) {
			candidate = candidate.firstChild;
		}
	}
	// The kind must match. A value asking for a different cell kind is a conversion
	// request: with `platform_editor_ai_cell_kind_conversion` on it is handled by
	// `prepareCellKindConversion` on its own transaction path, and with it off the
	// editor keeps the original kind. Neither case may adopt the value's id here.
	return candidate?.type === originalCell.type ? candidate : undefined;
}

/**
 * Resolves the cell wrapper that a whole-cell replacement targeted.
 *
 * `[startPos, endPos]` must be exactly the cell's inner range — that is the
 * signal that the tool call replaced the cell itself rather than editing part
 * of its content. Returns undefined for any other shape.
 */
function locateReplacedCell({
	originalDoc,
	startPos,
	endPos,
}: {
	endPos: number;
	originalDoc: PMNode;
	startPos: number;
}): { cellPos: number; originalCell: PMNode } | undefined {
	if (startPos < 0 || startPos > originalDoc.content.size) {
		return undefined;
	}

	const $start = originalDoc.resolve(startPos);
	const cellDepth = Array.from({ length: $start.depth }, (_, index) => $start.depth - index).find(
		(depth) =>
			$start.node(depth).type.name === 'tableCell' ||
			$start.node(depth).type.name === 'tableHeader',
	);
	if (cellDepth === undefined) {
		return undefined;
	}

	const cellPos = $start.before(cellDepth);
	const originalCell = $start.node(cellDepth);
	if (startPos !== cellPos + 1 || endPos !== cellPos + originalCell.nodeSize - 1) {
		return undefined;
	}

	return { cellPos, originalCell };
}

/**
 * CCI-20128: adopt the replacement cell's `localId` onto the existing cell wrapper.
 *
 * Replacing a whole `tableCell` / `tableHeader` targets the cell's inner range, so
 * `replaceRange` swaps the cell's children and keeps the original wrapper node — see
 * `getTableCellContentSlice` in `sliceAdjustments.ts`. The `localId` the backend minted
 * on the replacement cell was therefore dropped, and any later tool call in the same
 * session that targeted that new id could not resolve it (`toolCallResult` anomaly,
 * `symptom: local_id_not_found`).
 *
 * Replacing a `table` or a `tableRow` is unaffected — those swap the node itself, so
 * the value's ids come along with it.
 *
 * Only an explicit, non-empty `localId` on the replacement is adopted. A cell without
 * one deserialises to `localId: null` (the schema default), which must not clobber the
 * existing id.
 *
 * Also called from the legacy `useInEditorDirectStreaming` path, which is where the
 * streaming-replay fixtures exercise it.
 */
export function applyCellReplacementLocalId({
	tr,
	originalDoc,
	replacementContent,
	startPos,
	endPos,
}: {
	endPos: number;
	originalDoc: PMNode;
	replacementContent: Fragment;
	startPos: number;
	tr: Transaction;
}): Transaction {
	if (!fg('platform_editor_ai_replace_cell_local_id')) {
		return tr;
	}

	const located = locateReplacedCell({ originalDoc, startPos, endPos });
	if (!located) {
		return tr;
	}
	const { cellPos, originalCell } = located;

	const replacement = findReplacementCellForLocalId(replacementContent, originalCell);
	const replacementLocalId: unknown = replacement?.attrs?.localId;
	if (typeof replacementLocalId !== 'string' || replacementLocalId.length === 0) {
		return tr;
	}

	const currentCell = tr.doc.nodeAt(cellPos);
	if (currentCell?.type !== originalCell.type || currentCell.attrs.localId === replacementLocalId) {
		return tr;
	}

	return tr.setNodeMarkup(
		cellPos,
		undefined,
		{ ...currentCell.attrs, localId: replacementLocalId },
		currentCell.marks,
	);
}

/** Restore explicit cell attributes when replaceRange merges into the selected cell wrapper. */
export function applyCellReplacementAttributes({
	tr,
	originalDoc,
	replacementContent,
	adfResponse,
	startPos,
	endPos,
}: {
	adfResponse: ADFResponse | undefined;
	endPos: number;
	originalDoc: PMNode;
	replacementContent: Fragment;
	startPos: number;
	tr: Transaction;
}): Transaction {
	if (startPos < 0 || startPos > originalDoc.content.size) {
		return tr;
	}

	const $start = originalDoc.resolve(startPos);
	const cellDepth = Array.from({ length: $start.depth }, (_, index) => $start.depth - index).find(
		(depth) =>
			$start.node(depth).type.name === 'tableCell' ||
			$start.node(depth).type.name === 'tableHeader',
	);
	if (cellDepth === undefined) {
		return tr;
	}

	const cellPos = $start.before(cellDepth);
	const originalCell = $start.node(cellDepth);
	if (startPos !== cellPos + 1 || endPos !== cellPos + originalCell.nodeSize - 1) {
		return tr;
	}
	if (!isExperimentEnabled('platform_editor_ai_replace_cell_attrs_exp')) {
		return tr;
	}
	const replacement = findReplacementCell(replacementContent, originalCell);
	const rawNode = findRawCell(adfResponse as RawADFNode | undefined, originalCell);
	if (!replacement || !rawNode?.attrs) {
		return tr;
	}

	const currentCell = tr.doc.nodeAt(cellPos);
	if (currentCell?.type !== originalCell.type) {
		return tr;
	}

	const attrs = { ...currentCell.attrs };
	let changed = false;
	for (const name of ['background', 'valign'] as const) {
		if (!(name in rawNode.attrs) || !(name in replacement.attrs)) {
			continue;
		}
		const value = replacement.attrs[name];
		if (attrs[name] !== value) {
			attrs[name] = value;
			changed = true;
		}
	}

	return changed ? tr.setNodeMarkup(cellPos, undefined, attrs, currentCell.marks) : tr;
}
